import { useSyncExternalStore } from 'react'

type Cleanup = () => void

export const LCP_QUIET_MS = 300
export const LCP_MAX_WAIT_MS = 5000
export const IDLE_TIMEOUT_MS = 4000

function supportsLcpObserver(): boolean {
    return (
        typeof PerformanceObserver !== 'undefined' &&
        !!PerformanceObserver.supportedEntryTypes?.includes(
            'largest-contentful-paint',
        )
    )
}

function waitForStableLcp(
    quietMs = LCP_QUIET_MS,
    maxWaitMs = LCP_MAX_WAIT_MS,
): { promise: Promise<void>; cleanup: Cleanup } {
    if (typeof window === 'undefined' || !supportsLcpObserver()) {
        return { promise: Promise.resolve(), cleanup: () => {} }
    }

    let settled = false
    let quietTimer: ReturnType<typeof setTimeout> | undefined
    let maxTimer: ReturnType<typeof setTimeout> | undefined
    let observer: PerformanceObserver | undefined
    let resolvePromise: () => void = () => {}

    const cleanup = () => {
        if (settled) return
        settled = true
        observer?.disconnect()
        if (quietTimer !== undefined) clearTimeout(quietTimer)
        if (maxTimer !== undefined) clearTimeout(maxTimer)
        resolvePromise()
    }

    const promise = new Promise<void>((resolve) => {
        resolvePromise = resolve

        const scheduleQuietFinish = () => {
            if (quietTimer !== undefined) clearTimeout(quietTimer)
            quietTimer = setTimeout(cleanup, quietMs)
        }

        try {
            observer = new PerformanceObserver((list) => {
                if (list.getEntries().length > 0) scheduleQuietFinish()
            })
            observer.observe({
                type: 'largest-contentful-paint',
                buffered: true,
            })
        } catch {
            cleanup()
            return
        }

        maxTimer = setTimeout(cleanup, maxWaitMs)
    })

    return { promise, cleanup }
}

function waitForIdle(timeoutMs = IDLE_TIMEOUT_MS): {
    promise: Promise<void>
    cleanup: Cleanup
} {
    let cancelled = false
    let idleId: number | undefined
    let timeoutId: ReturnType<typeof setTimeout> | undefined

    const promise = new Promise<void>((resolve) => {
        const done = () => {
            if (cancelled) return
            resolve()
        }

        if (typeof window.requestIdleCallback === 'function') {
            idleId = window.requestIdleCallback(done, { timeout: timeoutMs })
            return
        }

        timeoutId = setTimeout(done, 0)
    })

    return {
        promise,
        cleanup: () => {
            cancelled = true
            if (idleId !== undefined) {
                window.cancelIdleCallback(idleId)
            }
            if (timeoutId !== undefined) clearTimeout(timeoutId)
        },
    }
}

export function schedulePixelScriptLoad(onReady: () => void): Cleanup {
    let cancelled = false
    const cleanups: Cleanup[] = []

    const run = async () => {
        const lcp = waitForStableLcp()
        cleanups.push(lcp.cleanup)
        await lcp.promise
        if (cancelled) return

        const idle = waitForIdle()
        cleanups.push(idle.cleanup)
        await idle.promise
        if (cancelled) return

        onReady()
    }

    void run()

    return () => {
        cancelled = true
        cleanups.forEach((fn) => fn())
    }
}

let scriptsReady = false
let started = false
let loadCleanup: Cleanup | undefined
const listeners = new Set<() => void>()

function subscribe(onStoreChange: () => void): Cleanup {
    listeners.add(onStoreChange)

    if (!started) {
        started = true
        loadCleanup = schedulePixelScriptLoad(() => {
            scriptsReady = true
            listeners.forEach((listener) => listener())
        })
    }

    return () => {
        listeners.delete(onStoreChange)
    }
}

export function whenPixelScriptsReady(fn: () => void): void {
    if (scriptsReady) {
        fn()
        return
    }

    const unsubscribe = subscribe(() => {
        if (!scriptsReady) return
        unsubscribe()
        fn()
    })
}

export function usePixelScriptsReady(): boolean {
    return useSyncExternalStore(
        subscribe,
        () => scriptsReady,
        () => false,
    )
}

export function resetPixelScriptScheduler(): void {
    loadCleanup?.()
    loadCleanup = undefined
    scriptsReady = false
    started = false
    listeners.clear()
}
